import React, { useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Checkbox,
  Button,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TableContainer,
} from "@mui/material";

import { Visibility, Edit, Delete } from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { Link } from "react-router-dom";
import "./HRSummary.css"; // Custom CSS file

// Dummy HR summary data
const hrSummaryData = [
  {
    id: 1,
    employee_id: "EMP001",
    name: "John Doe",
    employee_status: "Active",
    employee_category: "Full-Time",
    contact_number: "9876543210",
    email_id: "john.doe@example.com",
    date_of_joining: "2021-05-10",
    department: "Engineering",
    sub_department: "Software",
    designation: "Senior Developer",
    category_control: "Yes",
    joined_ctc: "8 LPA",
    current_ctc: "10 LPA",
    service_commitment: "2",
    date_of_birth: "1990-08-15",
    blood_group: "O+",
    fathers_name: "Richard Doe",
    pincode: "600001",
    address: "123, 1st Main Road, Cityville",
    familyId: 1,
  },
  {
    id: 2,
    employee_id: "EMP002",
    name: "Jane Smith",
    employee_status: "On Leave",
    employee_category: "Part-Time",
    contact_number: "9123456789",
    email_id: "jane.smith@example.com",
    date_of_joining: "2020-03-18",
    department: "Human Resources",
    sub_department: "Recruitment",
    designation: "HR Executive",
    category_control: "No",
    joined_ctc: "5 LPA",
    current_ctc: "6.5 LPA",
    service_commitment: "1",
    date_of_birth: "1992-11-22",
    blood_group: "A+",
    fathers_name: "Michael Smith",
    pincode: "500001",
    address: "45, Park Lane, Towncity",
    familyId: 2,
  },
  {
    id: 3,
    employee_id: "EMP003",
    name: "Alice Johnson",
    employee_status: "Resigned",
    employee_category: "Contract",
    contact_number: "9090909090",
    email_id: "alice.johnson@example.com",
    date_of_joining: "2019-07-01",
    department: "Finance",
    sub_department: "Accounts",
    designation: "Accountant",
    category_control: "Yes",
    joined_ctc: "6 LPA",
    current_ctc: "7.2 LPA",
    service_commitment: "3",
    date_of_birth: "1988-02-14",
    blood_group: "B+",
    fathers_name: "Robert Johnson",
    pincode: "700001",
    address: "67, Lake Road, Metropolis",
    familyId: 3,
  },
];

const HRSummary = () => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [year, setYear] = useState("2024");
  const [month, setMonth] = useState("January");

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(hrSummaryData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleImport = () => alert("Import clicked");
  const handleExport = () => alert("Export clicked");
  const handleView = (id) => alert(`View employee with familyId: ${id}`);
  const handleDelete = (id) => alert(`Delete employee with familyId: ${id}`);

  const isAllSelected = selectedRows.length === hrSummaryData.length;

  return (
    <Box className="hr-summary-container">
      {/* Filters */}
      <Box className="hr-summary-filters">
        <FormControl className="hr-summary-select">
          <InputLabel>Year</InputLabel>
          <Select value={year} onChange={(e) => setYear(e.target.value)}>
            <MenuItem value="2024">2024</MenuItem>
            <MenuItem value="2023">2023</MenuItem>
            <MenuItem value="2022">2022</MenuItem>
          </Select>
        </FormControl>

        <FormControl className="hr-summary-select">
          <InputLabel>Month</InputLabel>
          <Select value={month} onChange={(e) => setMonth(e.target.value)}>
            <MenuItem value="January">January</MenuItem>
            <MenuItem value="February">February</MenuItem>
            <MenuItem value="March">March</MenuItem>
            <MenuItem value="April">April</MenuItem>
            <MenuItem value="May">May</MenuItem>
            <MenuItem value="June">June</MenuItem>
          </Select>
        </FormControl>

        <Box className="hr-summary-buttons">
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            className="hr-btn-import"
            onClick={handleImport}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudDownloadIcon />}
            className="hr-btn-export"
            onClick={handleExport}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Box className="hr-summary-table">
        <TableContainer component={Paper} className="hr-summary-table-container">
          <Table stickyHeader>
            <TableHead>
              <TableRow className="hr-summary-header">
                <TableCell align="center">
                  <Checkbox
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    indeterminate={
                      selectedRows.length > 0 &&
                      selectedRows.length < hrSummaryData.length
                    }
                    size="small"
                  />
                </TableCell>
                {[
                  "S.NO",
                  "Employee ID",
                  "Employee Name",
                  "Status",
                  "Category",
                  "Contact Number",
                  "Email",
                  "Date Of Joining",
                  "Department",
                  "Sub Department",
                  "Designation",
                  "Category Control",
                  "Joined CTC",
                  "Current CTC",
                  "Service Commitment",
                  "DOB",
                  "Blood Group",
                  "Father Name",
                  "Pincode",
                  "Address",
                  "Action",
                ].map((header) => (
                  <TableCell key={header} align="center">
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {hrSummaryData.map((row, index) => (
                <TableRow key={row.id} className="hr-summary-row">
                  <TableCell align="center">
                    <Checkbox
                      checked={selectedRows.includes(row.id)}
                      onChange={() => handleRowSelect(row.id)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell align="center">{row.employee_id}</TableCell>
                  <TableCell align="center">
                    <Link to="#" className="hr-summary-link">
                      {row.name}
                    </Link>
                  </TableCell>
                  <TableCell align="center">{row.employee_status}</TableCell>
                  <TableCell align="center">{row.employee_category}</TableCell>
                  <TableCell align="center">{row.contact_number}</TableCell>
                  <TableCell align="center">{row.email_id}</TableCell>
                  <TableCell align="center">{row.date_of_joining}</TableCell>
                  <TableCell align="center">{row.department}</TableCell>
                  <TableCell align="center">{row.sub_department}</TableCell>
                  <TableCell align="center">{row.designation}</TableCell>
                  <TableCell align="center">{row.category_control}</TableCell>
                  <TableCell align="center">{row.joined_ctc}</TableCell>
                  <TableCell align="center">{row.current_ctc}</TableCell>
                  <TableCell align="center">{row.service_commitment}</TableCell>
                  <TableCell align="center">{row.date_of_birth}</TableCell>
                  <TableCell align="center">{row.blood_group}</TableCell>
                  <TableCell align="center">{row.fathers_name}</TableCell>
                  <TableCell align="center">{row.pincode}</TableCell>
                  <TableCell align="center">{row.address}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleView(row.familyId)}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                    <IconButton color="warning" size="small">
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleDelete(row.familyId)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default HRSummary;



// import React, { useState, useEffect } from "react";
// import {
//   Box,
//   Table,
//   TableHead,
//   TableRow,
//   TableCell,
//   TableBody,
//   Paper,
//   Checkbox,
//   Button,
//   IconButton,
//   Select,
//   MenuItem,
//   InputLabel,
//   FormControl,
//   TableContainer,
//   TextField,
//   Modal,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Typography,
//   Tooltip,
//   useMediaQuery,
//   useTheme,
// } from "@mui/material";

// import {
//   Visibility,
//   Edit,
//   Delete,
//   Save,
//   Close,
//   Search,
//   FilterList,
//   Download,
//   Upload,
// } from "@mui/icons-material";

// import "./HRSummary.css";

// // Generate dummy HR data from 2020–2026
// const hrSummaryData = [
//   {
//     id: 1,
//     employee_id: "EMP001",
//     name: "John Doe",
//     employee_status: "Active",
//     employee_category: "Full-Time",
//     contact_number: "9876543210",
//     email_id: "john.doe@example.com",
//     date_of_joining: "2020-05-10",
//     department: "Engineering",
//     sub_department: "Software",
//     designation: "Senior Developer",
//     category_control: "Yes",
//     joined_ctc: "8 LPA",
//     current_ctc: "10 LPA",
//     service_commitment: "2",
//     date_of_birth: "1990-08-15",
//     blood_group: "O+",
//     fathers_name: "Richard Doe",
//     pincode: "600001",
//     address: "123, 1st Main Road, Cityville",
//     familyId: 1,
//   },
//   {
//     id: 2,
//     employee_id: "EMP002",
//     name: "Jane Smith",
//     employee_status: "On Leave",
//     employee_category: "Part-Time",
//     contact_number: "9123456789",
//     email_id: "jane.smith@example.com",
//     date_of_joining: "2021-03-18",
//     department: "Human Resources",
//     sub_department: "Recruitment",
//     designation: "HR Executive",
//     category_control: "No",
//     joined_ctc: "5 LPA",
//     current_ctc: "6.5 LPA",
//     service_commitment: "1",
//     date_of_birth: "1992-11-22",
//     blood_group: "A+",
//     fathers_name: "Michael Smith",
//     pincode: "500001",
//     address: "45, Park Lane, Towncity",
//     familyId: 2,
//   },
//   {
//     id: 3,
//     employee_id: "EMP003",
//     name: "Alice Johnson",
//     employee_status: "Resigned",
//     employee_category: "Contract",
//     contact_number: "9090909090",
//     email_id: "alice.johnson@example.com",
//     date_of_joining: "2022-07-01",
//     department: "Finance",
//     sub_department: "Accounts",
//     designation: "Accountant",
//     category_control: "Yes",
//     joined_ctc: "6 LPA",
//     current_ctc: "7.2 LPA",
//     service_commitment: "3",
//     date_of_birth: "1988-02-14",
//     blood_group: "B+",
//     fathers_name: "Robert Johnson",
//     pincode: "700001",
//     address: "67, Lake Road, Metropolis",
//     familyId: 3,
//   },
//   {
//     id: 4,
//     employee_id: "EMP004",
//     name: "Michael Brown",
//     employee_status: "Active",
//     employee_category: "Full-Time",
//     contact_number: "9988776655",
//     email_id: "michael.brown@example.com",
//     date_of_joining: "2023-01-12",
//     department: "Engineering",
//     sub_department: "DevOps",
//     designation: "Engineer",
//     category_control: "Yes",
//     joined_ctc: "7 LPA",
//     current_ctc: "8.5 LPA",
//     service_commitment: "2",
//     date_of_birth: "1991-06-10",
//     blood_group: "A+",
//     fathers_name: "David Brown",
//     pincode: "600002",
//     address: "89, Second Street, Cityville",
//     familyId: 4,
//   },
//   {
//     id: 5,
//     employee_id: "EMP005",
//     name: "Emily Davis",
//     employee_status: "On Leave",
//     employee_category: "Part-Time",
//     contact_number: "9012345678",
//     email_id: "emily.davis@example.com",
//     date_of_joining: "2024-09-05",
//     department: "Marketing",
//     sub_department: "Digital",
//     designation: "Coordinator",
//     category_control: "No",
//     joined_ctc: "4 LPA",
//     current_ctc: "5 LPA",
//     service_commitment: "1",
//     date_of_birth: "1993-03-21",
//     blood_group: "B+",
//     fathers_name: "George Davis",
//     pincode: "500002",
//     address: "12, Park Avenue, Towncity",
//     familyId: 5,
//   },
//   {
//     id: 6,
//     employee_id: "EMP006",
//     name: "Robert Wilson",
//     employee_status: "Active",
//     employee_category: "Full-Time",
//     contact_number: "9871234567",
//     email_id: "robert.wilson@example.com",
//     date_of_joining: "2025-04-20",
//     department: "Finance",
//     sub_department: "Accounts",
//     designation: "Manager",
//     category_control: "Yes",
//     joined_ctc: "9 LPA",
//     current_ctc: "11 LPA",
//     service_commitment: "3",
//     date_of_birth: "1987-12-11",
//     blood_group: "O+",
//     fathers_name: "Henry Wilson",
//     pincode: "700002",
//     address: "34, Lake Street, Metropolis",
//     familyId: 6,
//   },
//   {
//     id: 7,
//     employee_id: "EMP007",
//     name: "Sophia Lee",
//     employee_status: "Resigned",
//     employee_category: "Contract",
//     contact_number: "9123987654",
//     email_id: "sophia.lee@example.com",
//     date_of_joining: "2026-02-15",
//     department: "HR",
//     sub_department: "Recruitment",
//     designation: "Executive",
//     category_control: "No",
//     joined_ctc: "5.5 LPA",
//     current_ctc: "6.5 LPA",
//     service_commitment: "2",
//     date_of_birth: "1992-09-12",
//     blood_group: "A+",
//     fathers_name: "Edward Lee",
//     pincode: "500003",
//     address: "78, Green Street, Towncity",
//     familyId: 7,
//   },
// ];

// const HRSummary = () => {
//   const [data, setData] = useState(hrSummaryData);
//   const [selectedRows, setSelectedRows] = useState([]);
//   const [year, setYear] = useState("2020");
//   const [month, setMonth] = useState("January");
//   const [search, setSearch] = useState("");
//   const [editRowId, setEditRowId] = useState(null);
//   const [editValues, setEditValues] = useState({});
//   const [viewData, setViewData] = useState(null);
//   const [isFilterOpen, setIsFilterOpen] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [itemsPerPage] = useState(10);

//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

//   // Filter by Year, Month & Search
//   const filteredData = data.filter((row) => {
//     const joinYear = new Date(row.date_of_joining).getFullYear().toString();
//     const joinMonth = new Date(row.date_of_joining).toLocaleString("default", {
//       month: "long",
//     });

//     const matchesYearMonth = joinYear === year && joinMonth === month;

//     const matchesSearch =
//       row.name.toLowerCase().includes(search.toLowerCase()) ||
//       row.contact_number.includes(search) ||
//       row.date_of_joining.includes(search) ||
//       row.employee_id.toLowerCase().includes(search.toLowerCase()) ||
//       row.email_id.toLowerCase().includes(search.toLowerCase());

//     return matchesYearMonth && matchesSearch;
//   });

//   // Pagination
//   const totalPages = Math.ceil(filteredData.length / itemsPerPage);
//   const startIndex = (currentPage - 1) * itemsPerPage;
//   const currentData = filteredData.slice(startIndex, startIndex + itemsPerPage);

//   const handleSelectAll = (event) => {
//     if (event.target.checked) {
//       setSelectedRows(currentData.map((row) => row.id));
//     } else {
//       setSelectedRows([]);
//     }
//   };

//   const handleRowSelect = (id) => {
//     setSelectedRows((prev) =>
//       prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
//     );
//   };

//   const handleImport = (e) => {
//     const file = e.target.files[0];
//     if (!file) return;
//     const reader = new FileReader();
//     reader.onload = (evt) => {
//       const text = evt.target.result;
//       const rows = text.split("\n").slice(1); // skip header
//       const importedData = rows.map((r, i) => {
//         const cols = r.split(",");
//         return {
//           id: data.length + i + 1,
//           employee_id: cols[0] || `EMP${data.length + i + 1}`,
//           name: cols[1] || `Employee ${data.length + i + 1}`,
//           employee_status: cols[2] || "Active",
//           employee_category: cols[3] || "Full-Time",
//           contact_number: cols[4] || "0000000000",
//           email_id: cols[5] || `emp${data.length + i + 1}@example.com`,
//           date_of_joining: cols[6] || "2023-01-01",
//           department: cols[7] || "Department",
//           sub_department: cols[8] || "Sub Department",
//           designation: cols[9] || "Employee",
//           category_control: cols[10] || "No",
//           joined_ctc: cols[11] || "5 LPA",
//           current_ctc: cols[12] || "6 LPA",
//           service_commitment: cols[13] || "1",
//           date_of_birth: cols[14] || "1990-01-01",
//           blood_group: cols[15] || "O+",
//           fathers_name: cols[16] || "Father Name",
//           pincode: cols[17] || "000000",
//           address: cols[18] || "Address",
//           familyId: data.length + i + 1,
//         };
//       });
//       setData([...data, ...importedData]);
//     };
//     reader.readAsText(file);
//   };

//   const handleExport = () => {
//     const headers = [
//       "Employee ID",
//       "Name",
//       "Status",
//       "Category",
//       "Contact",
//       "Email",
//       "DOJ",
//       "Department",
//       "Sub Department",
//       "Designation",
//       "Category Control",
//       "Joined CTC",
//       "Current CTC",
//       "Service Commitment",
//       "DOB",
//       "Blood Group",
//       "Father Name",
//       "Pincode",
//       "Address",
//     ];
//     const rows = filteredData.map((r) =>
//       [
//         r.employee_id,
//         r.name,
//         r.employee_status,
//         r.employee_category,
//         r.contact_number,
//         r.email_id,
//         r.date_of_joining,
//         r.department,
//         r.sub_department,
//         r.designation,
//         r.category_control,
//         r.joined_ctc,
//         r.current_ctc,
//         r.service_commitment,
//         r.date_of_birth,
//         r.blood_group,
//         r.fathers_name,
//         r.pincode,
//         r.address,
//       ].join(",")
//     );
//     const csvContent = [headers.join(","), ...rows].join("\n");
//     const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
//     const url = URL.createObjectURL(blob);
//     const link = document.createElement("a");
//     link.href = url;
//     link.setAttribute("download", `HR_Summary_${year}_${month}.csv`);
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   };

//   const handleView = (row) => setViewData(row);
//   const handleCloseView = () => setViewData(null);

//   const handleEdit = (row) => {
//     setEditRowId(row.id);
//     setEditValues({
//       name: row.name,
//       employee_status: row.employee_status,
//       current_ctc: row.current_ctc,
//       contact_number: row.contact_number,
//       email_id: row.email_id,
//     });
//   };

//   const handleSave = (id) => {
//     setData(
//       data.map((row) => (row.id === id ? { ...row, ...editValues } : row))
//     );
//     setEditRowId(null);
//   };

//   const handleDelete = (id) => {
//     if (window.confirm("Are you sure you want to delete this employee?")) {
//       setData(data.filter((row) => row.familyId !== id));
//       setSelectedRows(selectedRows.filter(rowId => rowId !== id));
//     }
//   };

//   const handlePageChange = (newPage) => {
//     setCurrentPage(newPage);
//   };

//   const isAllSelected =
//     selectedRows.length > 0 && selectedRows.length === currentData.length;

//   const tableHeaders = [
//     { key: 'sno', label: 'S.NO' },
//     { key: 'employee_id', label: 'Employee ID' },
//     { key: 'name', label: 'Employee Name' },
//     { key: 'employee_status', label: 'Status' },
//     { key: 'employee_category', label: 'Category' },
//     { key: 'contact_number', label: 'Contact Number' },
//     { key: 'email_id', label: 'Email' },
//     { key: 'date_of_joining', label: 'Date Of Joining' },
//     { key: 'department', label: 'Department' },
//     { key: 'sub_department', label: 'Sub Department' },
//     { key: 'designation', label: 'Designation' },
//     { key: 'category_control', label: 'Category Control' },
//     { key: 'joined_ctc', label: 'Joined CTC' },
//     { key: 'current_ctc', label: 'Current CTC' },
//     { key: 'service_commitment', label: 'Service Commitment' },
//     { key: 'date_of_birth', label: 'DOB' },
//     { key: 'blood_group', label: 'Blood Group' },
//     { key: 'fathers_name', label: 'Father Name' },
//     { key: 'pincode', label: 'Pincode' },
//     { key: 'address', label: 'Address' },
//   ];

//   return (
//     <Box className="hr-summary-container">
//       {/* Header Section */}
//       {/* <Box className="hr-summary-header">
//         <Typography variant="h5" className="hr-summary-title">
//           HR Summary
//         </Typography>
//         <Box className="hr-summary-stats">
//           <span>Total Employees: {filteredData.length}</span>
//           <span>Active: {filteredData.filter(d => d.employee_status === 'Active').length}</span>
//         </Box>
//       </Box> */}

//       {/* Filters Section */}
//       <Box className="hr-summary-filters">
//         <Box className="hr-summary-search-container">
//           <Search className="search-icon" />
//           <TextField
//             fullWidth
//             placeholder="Search by Name, ID, Mobile, Email, DOJ..."
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//             size="small"
//             variant="outlined"
//             className="hr-summary-search"
//           />
//         </Box>

//         {!isMobile ? (
//           <Box className="hr-summary-filter-controls">
//             <FormControl className="hr-summary-select" size="small">
//               <InputLabel>Year</InputLabel>
//               <Select value={year} onChange={(e) => setYear(e.target.value)} label="Year">
//                 {["2020", "2021", "2022", "2023", "2024", "2025", "2026"].map(
//                   (y) => (
//                     <MenuItem key={y} value={y}>
//                       {y}
//                     </MenuItem>
//                   )
//                 )}
//               </Select>
//             </FormControl>

//             <FormControl className="hr-summary-select" size="small">
//               <InputLabel>Month</InputLabel>
//               <Select value={month} onChange={(e) => setMonth(e.target.value)} label="Month">
//                 {[
//                   "January",
//                   "February",
//                   "March",
//                   "April",
//                   "May",
//                   "June",
//                   "July",
//                   "August",
//                   "September",
//                   "October",
//                   "November",
//                   "December",
//                 ].map((m) => (
//                   <MenuItem key={m} value={m}>
//                     {m}
//                   </MenuItem>
//                 ))}
//               </Select>
//             </FormControl>

//             <Box className="hr-summary-buttons">
//               <Tooltip title="Import CSV">
//                 <Button
//                   variant="outlined"
//                   component="label"
//                   startIcon={<Upload />}
//                   className="hr-btn-import"
//                 >
//                   {!isTablet && "Import"}
//                   <input type="file" hidden accept=".csv" onChange={handleImport} />
//                 </Button>
//               </Tooltip>
              
//               <Tooltip title="Export CSV">
//                 <Button
//                   variant="contained"
//                   startIcon={<Download />}
//                   className="hr-btn-export"
//                   onClick={handleExport}
//                 >
//                   {!isTablet && "Export"}
//                 </Button>
//               </Tooltip>
//             </Box>
//           </Box>
//         ) : (
//           <>
//             <Button
//               variant="outlined"
//               startIcon={<FilterList />}
//               onClick={() => setIsFilterOpen(true)}
//               className="mobile-filter-btn"
//             >
//               Filters
//             </Button>
            
//             <Box className="mobile-action-buttons">
//               <Tooltip title="Import">
//                 <IconButton component="label" className="mobile-action-btn">
//                   <Upload />
//                   <input type="file" hidden accept=".csv" onChange={handleImport} />
//                 </IconButton>
//               </Tooltip>
              
//               <Tooltip title="Export">
//                 <IconButton className="mobile-action-btn" onClick={handleExport}>
//                   <Download />
//                 </IconButton>
//               </Tooltip>
//             </Box>
//           </>
//         )}
//       </Box>

//       {/* Filter Modal for Mobile */}
//       <Dialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)}>
//         <DialogTitle>Filter Options</DialogTitle>
//         <DialogContent>
//           <FormControl fullWidth margin="normal" size="small">
//             <InputLabel>Year</InputLabel>
//             <Select value={year} onChange={(e) => setYear(e.target.value)}>
//               {["2020", "2021", "2022", "2023", "2024", "2025", "2026"].map(
//                 (y) => (
//                   <MenuItem key={y} value={y}>
//                     {y}
//                   </MenuItem>
//                 )
//               )}
//             </Select>
//           </FormControl>

//           <FormControl fullWidth margin="normal" size="small">
//             <InputLabel>Month</InputLabel>
//             <Select value={month} onChange={(e) => setMonth(e.target.value)}>
//               {[
//                 "January",
//                 "February",
//                 "March",
//                 "April",
//                 "May",
//                 "June",
//                 "July",
//                 "August",
//                 "September",
//                 "October",
//                 "November",
//                 "December",
//               ].map((m) => (
//                 <MenuItem key={m} value={m}>
//                   {m}
//                 </MenuItem>
//               ))}
//             </Select>
//           </FormControl>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={() => setIsFilterOpen(false)}>Close</Button>
//           <Button onClick={() => setIsFilterOpen(false)} variant="contained">
//             Apply
//           </Button>
//         </DialogActions>
//       </Dialog>

//       {/* Table Section */}
//       <Box className="hr-summary-table-container">
//         <TableContainer component={Paper} className="hr-summary-table-wrapper">
//           <Box className="table-responsive-wrapper">
//             <Table stickyHeader className="hr-summary-table">
//               <TableHead>
//                 <TableRow className="hr-summary-table-header">
//                   <TableCell className="checkbox-cell" align="center">
//                     <Checkbox
//                       checked={isAllSelected}
//                       onChange={handleSelectAll}
//                       indeterminate={
//                         selectedRows.length > 0 &&
//                         selectedRows.length < currentData.length
//                       }
//                       size="small"
//                     />
//                   </TableCell>
//                   {tableHeaders.map((header) => (
//                     <TableCell 
//                       key={header.key} 
//                       align="center"
//                       className={`table-header-cell ${isMobile ? 'mobile-header' : ''}`}
//                     >
//                       {isMobile ? header.label.substring(0, 3) + '.' : header.label}
//                     </TableCell>
//                   ))}
//                   <TableCell align="center" className="action-cell">Action</TableCell>
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {currentData.length === 0 ? (
//                   <TableRow>
//                     <TableCell colSpan={22} align="center" className="no-data-cell">
//                       <Box className="no-data-message">
//                         <Typography variant="h6" color="textSecondary">
//                           No records found
//                         </Typography>
//                         <Typography variant="body2" color="textSecondary">
//                           Try changing your search or filter criteria
//                         </Typography>
//                       </Box>
//                     </TableCell>
//                   </TableRow>
//                 ) : (
//                   currentData.map((row, index) => (
//                     <TableRow key={row.id} className="hr-summary-table-row">
//                       <TableCell align="center" className="checkbox-cell">
//                         <Checkbox
//                           checked={selectedRows.includes(row.id)}
//                           onChange={() => handleRowSelect(row.id)}
//                           size="small"
//                         />
//                       </TableCell>
//                       <TableCell align="center">{startIndex + index + 1}</TableCell>
//                       <TableCell align="center">{row.employee_id}</TableCell>
//                       <TableCell align="center">
//                         {editRowId === row.id ? (
//                           <TextField
//                             value={editValues.name}
//                             onChange={(e) =>
//                               setEditValues((prev) => ({
//                                 ...prev,
//                                 name: e.target.value,
//                               }))
//                             }
//                             size="small"
//                             className="edit-field"
//                           />
//                         ) : (
//                           <span 
//                             className="employee-name-link"
//                             onClick={() => handleView(row)}
//                           >
//                             {row.name}
//                           </span>
//                         )}
//                       </TableCell>
//                       <TableCell align="center">
//                         {editRowId === row.id ? (
//                           <Select
//                             value={editValues.employee_status}
//                             onChange={(e) =>
//                               setEditValues((prev) => ({
//                                 ...prev,
//                                 employee_status: e.target.value,
//                               }))
//                             }
//                             size="small"
//                             className="edit-field"
//                           >
//                             <MenuItem value="Active">Active</MenuItem>
//                             <MenuItem value="On Leave">On Leave</MenuItem>
//                             <MenuItem value="Resigned">Resigned</MenuItem>
//                           </Select>
//                         ) : (
//                           <span className={`status-badge status-${row.employee_status.toLowerCase().replace(' ', '-')}`}>
//                             {row.employee_status}
//                           </span>
//                         )}
//                       </TableCell>
//                       <TableCell align="center">
//                         <span className="category-badge">{row.employee_category}</span>
//                       </TableCell>
//                       <TableCell align="center">
//                         {editRowId === row.id ? (
//                           <TextField
//                             value={editValues.contact_number}
//                             onChange={(e) =>
//                               setEditValues((prev) => ({
//                                 ...prev,
//                                 contact_number: e.target.value,
//                               }))
//                             }
//                             size="small"
//                             className="edit-field"
//                           />
//                         ) : (
//                           row.contact_number
//                         )}
//                       </TableCell>
//                       <TableCell align="center">
//                         {editRowId === row.id ? (
//                           <TextField
//                             value={editValues.email_id}
//                             onChange={(e) =>
//                               setEditValues((prev) => ({
//                                 ...prev,
//                                 email_id: e.target.value,
//                               }))
//                             }
//                             size="small"
//                             className="edit-field"
//                           />
//                         ) : (
//                           <a href={`mailto:${row.email_id}`} className="email-link">
//                             {row.email_id}
//                           </a>
//                         )}
//                       </TableCell>
//                       <TableCell align="center">{row.date_of_joining}</TableCell>
//                       <TableCell align="center">{row.department}</TableCell>
//                       <TableCell align="center">{row.sub_department}</TableCell>
//                       <TableCell align="center">{row.designation}</TableCell>
//                       <TableCell align="center">
//                         <span className={`control-badge ${row.category_control === 'Yes' ? 'control-yes' : 'control-no'}`}>
//                           {row.category_control}
//                         </span>
//                       </TableCell>
//                       <TableCell align="center">{row.joined_ctc}</TableCell>
//                       <TableCell align="center">
//                         {editRowId === row.id ? (
//                           <TextField
//                             value={editValues.current_ctc}
//                             onChange={(e) =>
//                               setEditValues((prev) => ({
//                                 ...prev,
//                                 current_ctc: e.target.value,
//                               }))
//                             }
//                             size="small"
//                             className="edit-field"
//                           />
//                         ) : (
//                           row.current_ctc
//                         )}
//                       </TableCell>
//                       <TableCell align="center">{row.service_commitment}</TableCell>
//                       <TableCell align="center">{row.date_of_birth}</TableCell>
//                       <TableCell align="center">
//                         <span className="blood-group-badge">{row.blood_group}</span>
//                       </TableCell>
//                       <TableCell align="center">{row.fathers_name}</TableCell>
//                       <TableCell align="center">{row.pincode}</TableCell>
//                       <TableCell align="center" className="address-cell">
//                         {isMobile ? `${row.address.substring(0, 15)}...` : row.address}
//                       </TableCell>
//                       <TableCell align="center" className="action-cell">
//                         <Box className="action-buttons">
//                           {editRowId === row.id ? (
//                             <>
//                               <Tooltip title="Save">
//                                 <IconButton
//                                   color="success"
//                                   onClick={() => handleSave(row.id)}
//                                   size="small"
//                                 >
//                                   <Save fontSize="small" />
//                                 </IconButton>
//                               </Tooltip>
//                               <Tooltip title="Cancel">
//                                 <IconButton
//                                   color="error"
//                                   onClick={() => setEditRowId(null)}
//                                   size="small"
//                                 >
//                                   <Close fontSize="small" />
//                                 </IconButton>
//                               </Tooltip>
//                             </>
//                           ) : (
//                             <>
//                               <Tooltip title="View">
//                                 <IconButton
//                                   color="primary"
//                                   onClick={() => handleView(row)}
//                                   size="small"
//                                 >
//                                   <Visibility fontSize="small" />
//                                 </IconButton>
//                               </Tooltip>
//                               <Tooltip title="Edit">
//                                 <IconButton
//                                   color="warning"
//                                   onClick={() => handleEdit(row)}
//                                   size="small"
//                                 >
//                                   <Edit fontSize="small" />
//                                 </IconButton>
//                               </Tooltip>
//                               <Tooltip title="Delete">
//                                 <IconButton
//                                   color="error"
//                                   onClick={() => handleDelete(row.familyId)}
//                                   size="small"
//                                 >
//                                   <Delete fontSize="small" />
//                                 </IconButton>
//                               </Tooltip>
//                             </>
//                           )}
//                         </Box>
//                       </TableCell>
//                     </TableRow>
//                   ))
//                 )}
//               </TableBody>
//             </Table>
//           </Box>
//         </TableContainer>

//         {/* Pagination */}
//         {filteredData.length > 0 && (
//           <Box className="pagination-container">
//             <Button
//               disabled={currentPage === 1}
//               onClick={() => handlePageChange(currentPage - 1)}
//               className="pagination-btn"
//             >
//               Previous
//             </Button>
            
//             <Box className="page-numbers">
//               {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
//                 let pageNum;
//                 if (totalPages <= 5) {
//                   pageNum = i + 1;
//                 } else if (currentPage <= 3) {
//                   pageNum = i + 1;
//                 } else if (currentPage >= totalPages - 2) {
//                   pageNum = totalPages - 4 + i;
//                 } else {
//                   pageNum = currentPage - 2 + i;
//                 }
                
//                 return (
//                   <Button
//                     key={pageNum}
//                     onClick={() => handlePageChange(pageNum)}
//                     className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
//                   >
//                     {pageNum}
//                   </Button>
//                 );
//               })}
//             </Box>
            
//             <Button
//               disabled={currentPage === totalPages}
//               onClick={() => handlePageChange(currentPage + 1)}
//               className="pagination-btn"
//             >
//               Next
//             </Button>
            
//             <Typography variant="body2" className="page-info">
//               Page {currentPage} of {totalPages}
//             </Typography>
//           </Box>
//         )}
//       </Box>

//       {/* View Modal */}
//       <Modal open={!!viewData} onClose={handleCloseView} className="view-modal">
//         <Box className="view-modal-content">
//           {viewData && (
//             <>
//               <Box className="modal-header">
//                 <Typography variant="h6">Employee Details</Typography>
//                 <IconButton onClick={handleCloseView} size="small">
//                   <Close />
//                 </IconButton>
//               </Box>
              
//               <Box className="modal-body">
//                 <Box className="employee-profile-header">
//                   <Typography variant="h5">{viewData.name}</Typography>
//                   <Typography variant="body2" color="textSecondary">
//                     {viewData.employee_id} • {viewData.designation}
//                   </Typography>
//                 </Box>
                
//                 <Box className="details-grid">
//                   <Box className="detail-section">
//                     <Typography variant="subtitle2" className="section-title">
//                       Personal Information
//                     </Typography>
//                     <DetailItem label="Employee Status" value={viewData.employee_status} />
//                     <DetailItem label="Category" value={viewData.employee_category} />
//                     <DetailItem label="Date of Birth" value={viewData.date_of_birth} />
//                     <DetailItem label="Blood Group" value={viewData.blood_group} />
//                     <DetailItem label="Father's Name" value={viewData.fathers_name} />
//                   </Box>
                  
//                   <Box className="detail-section">
//                     <Typography variant="subtitle2" className="section-title">
//                       Contact Information
//                     </Typography>
//                     <DetailItem label="Contact Number" value={viewData.contact_number} />
//                     <DetailItem label="Email" value={viewData.email_id} />
//                     <DetailItem label="Address" value={viewData.address} />
//                     <DetailItem label="Pincode" value={viewData.pincode} />
//                   </Box>
                  
//                   <Box className="detail-section">
//                     <Typography variant="subtitle2" className="section-title">
//                       Employment Details
//                     </Typography>
//                     <DetailItem label="Date of Joining" value={viewData.date_of_joining} />
//                     <DetailItem label="Department" value={viewData.department} />
//                     <DetailItem label="Sub Department" value={viewData.sub_department} />
//                     <DetailItem label="Designation" value={viewData.designation} />
//                     <DetailItem label="Service Commitment" value={`${viewData.service_commitment} years`} />
//                   </Box>
                  
//                   <Box className="detail-section">
//                     <Typography variant="subtitle2" className="section-title">
//                       Compensation
//                     </Typography>
//                     <DetailItem label="Category Control" value={viewData.category_control} />
//                     <DetailItem label="Joined CTC" value={viewData.joined_ctc} />
//                     <DetailItem label="Current CTC" value={viewData.current_ctc} />
//                   </Box>
//                 </Box>
//               </Box>
              
//               <Box className="modal-footer">
//                 <Button
//                   variant="contained"
//                   color="primary"
//                   onClick={handleCloseView}
//                 >
//                   Close
//                 </Button>
//               </Box>
//             </>
//           )}
//         </Box>
//       </Modal>
//     </Box>
//   );
// };

// // Helper component for detail items in modal
// const DetailItem = ({ label, value }) => (
//   <Box className="detail-item">
//     <Typography variant="body2" className="detail-label">
//       {label}:
//     </Typography>
//     <Typography variant="body1" className="detail-value">
//       {value}
//     </Typography>
//   </Box>
// );

// export default HRSummary;
